import type { Goal, HealthArea, Routine } from "instantdb/types";
import { instantClientDB } from "instantdb/instantClient";
import { id } from "@instantdb/admin";
import { sortItemsByOrder } from "@/utilities/sortItemsByOrder";

// Get all healthAreas
export function getHealthAreas(userId?: string) {
  const { data, isLoading } = instantClientDB.useQuery({
    healthAreas: {
      $: {
        where: {
          userId: userId ?? "",
        },
      },
      goals: {},
      routines: {
        daysCompleted: {},
      },
    },
  });

  if (!userId) {
    return {
      healthAreas: [],
      isLoading: false,
    };
  }

  // Sort routines for each goal based on routineOrder
  const healthAreas = (data?.healthAreas ?? [])
    .map((healthArea) => {
      if (!healthArea) {
        return null;
      }

      return {
        ...healthArea,
        goals: sortItemsByOrder(healthArea.goals, healthArea.goalOrder),
        routines: sortItemsByOrder(healthArea.routines, healthArea.routineOrder),
      };
    })
    .filter(Boolean);

  return {
    healthAreas: (healthAreas || []) as HealthArea[],
    isLoading: !!userId && isLoading,
  };
}

// Only update the healthArea, not the goals or routines
export async function upsertHealthArea(healthArea: HealthArea) {
  const {
    id: inputId,
    // @ts-expect-error goals and routines are not needed for healthArea
    goals: desiredGoals,
    // @ts-expect-error goals and routines are not needed for healthArea
    routines: desiredRoutines,
    ...baseHealthAreaData
  } = healthArea;

  // Ensure healthAreaId is generated only once if inputId is null/undefined
  const healthAreaId = inputId ?? id();

  await instantClientDB.transact([
    instantClientDB.tx.healthAreas[healthAreaId].update(baseHealthAreaData),
  ]);
}

// Create or update a goal, synchronizing linked goals and routines
export async function upsertHealthAreaGoalsAndRoutines(
  healthArea: HealthArea & { goals?: Goal[]; routines?: Routine[] },
) {
  const {
    id: inputId,
    goals: desiredGoals,
    routines: desiredRoutines,
    ...baseHealthAreaData
  } = healthArea;
  // Ensure healthAreaId is generated only once if inputId is null/undefined
  const healthAreaId = inputId ?? id();

  // Store the order of goals if they are provided
  if (desiredGoals?.length) {
    baseHealthAreaData.goalOrder = desiredGoals.map((goal) => goal.id);
  }

  // Store the order of routines if they are provided
  if (desiredRoutines?.length) {
    baseHealthAreaData.routineOrder = desiredRoutines.map((routine) => routine.id);
  }

  // --- Fetch current state using queryOnce ---
  let currentGoalIds: string[] = [];
  let currentRoutineIds: string[] = [];
  try {
    // Use queryOnce to fetch data imperatively
    const currentData = await instantClientDB.queryOnce({
      healthAreas: {
        $: { where: { id: healthAreaId } },
        goals: {}, // Fetch the full linked goal entities
        routines: {}, // Fetch the full linked routine entities
      },
    });

    // Extract current goal and routine IDs from the full objects
    const currentHealthArea = currentData?.data?.healthAreas?.[0];
    currentGoalIds = currentHealthArea?.goals?.map((goal: Goal) => goal.id) ?? [];
    currentRoutineIds = currentHealthArea?.routines?.map((routine: Routine) => routine.id) ?? [];
  } catch (error) {
    console.error("Error fetching current health area state with queryOnce:", error);
    throw new Error(
      `Failed to fetch current health area state for ${healthAreaId} using queryOnce: ${error}`,
    );
  }

  // --- Calculate desired state ---
  const desiredGoalIds = desiredGoals?.map((g) => g.id) ?? [];
  const desiredRoutineIds = desiredRoutines?.map((r) => r.id) ?? [];

  // --- Calculate differences ---
  const currentGoalIdSet = new Set(currentGoalIds);
  const desiredGoalIdSet = new Set(desiredGoalIds);

  const currentRoutineIdSet = new Set(currentRoutineIds);
  const desiredRoutineIdSet = new Set(desiredRoutineIds);

  const goalsToLink = desiredGoalIds.filter((id) => !currentGoalIdSet.has(id));
  const goalsToUnlink = currentGoalIds.filter((id) => !desiredGoalIdSet.has(id));

  const routinesToLink = desiredRoutineIds.filter((id) => !currentRoutineIdSet.has(id));
  const routinesToUnlink = currentRoutineIds.filter((id) => !desiredRoutineIdSet.has(id));

  // --- Build Transaction ---
  const txSteps = [
    // Step 1: Update base properties (including goalOrder and routineOrder)
    instantClientDB.tx.healthAreas[healthAreaId].update(baseHealthAreaData),
  ];

  // Step 2: Unlink goals and routines no longer desired
  if (goalsToUnlink.length > 0) {
    txSteps.push(instantClientDB.tx.healthAreas[healthAreaId].unlink({ goals: goalsToUnlink }));
  }

  if (routinesToUnlink.length > 0) {
    txSteps.push(
      instantClientDB.tx.healthAreas[healthAreaId].unlink({ routines: routinesToUnlink }),
    );
  }

  // Step 3: Link new goals and routines
  if (goalsToLink.length > 0) {
    txSteps.push(instantClientDB.tx.healthAreas[healthAreaId].link({ goals: goalsToLink }));
  }

  if (routinesToLink.length > 0) {
    txSteps.push(instantClientDB.tx.healthAreas[healthAreaId].link({ routines: routinesToLink }));
  }

  // --- Execute Transaction ---
  // Only transact if there are actual changes to make (link/unlink or potential base data changes)
  if (
    goalsToLink.length > 0 ||
    goalsToUnlink.length > 0 ||
    routinesToLink.length > 0 ||
    routinesToUnlink.length > 0 ||
    Object.keys(baseHealthAreaData).length > 0
  ) {
    // Infer the type for the transaction steps array
    type TxStep = (typeof txSteps)[number];
    try {
      await instantClientDB.transact(txSteps as TxStep[]);
    } catch (error) {
      console.error("Error upserting healthArea transaction:", healthArea, error);
      // Rethrow the error to be handled by the caller
      throw error;
    }
  }
}

// Delete a healthArea
export async function deleteHealthArea(id: string) {
  await instantClientDB.transact([instantClientDB.tx.healthAreas[id].delete()]);
}
